import { useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';

import { Controller, useFieldArray, useForm } from 'react-hook-form';

import FileUpload from '@/components/ui/FileUpload';

import Input from '@/components/ui/Input/Input';
import { useApplyToJob } from '@/lib/api/hooks/job'

import { useFileUpload } from '@/lib/api/hooks/file-upload';

import { JobApplicationSchema, type JobApplication } from '@/lib/api/types';
import { Heading, Paragraph } from '@/components/ui/Typography';
import CustomSelect from '@/components/ui/CustomSelect';
import Button from '@/components/ui/Button';
import { LucideFiles, Plus, Trash2 } from 'lucide-react';
import {
  defaultCountries,
  parseCountry,
  type ParsedCountry
} from "react-international-phone";
import { toast } from 'sonner';
import CustomPhoneInput from '@/components/ui/PhoneInput/CustomPhoneInput';

const SectionHeader = ({ index, title }: { index: number; title: string }) => (
  <div className="mb-4 flex items-center gap-3">
    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#681628] text-xs font-semibold text-white">
      {index}
    </div>
    <Heading level={5} className="text-gray-900">
      {title}
    </Heading>
  </div>
);

interface UploadedFile {
  name: string;
  size: number;
  url: string;
  file: File | null;
}
const ApplicationForm = ({ jobId }: { jobId: string }) => {

    const cvUploadRef = useRef<HTMLDivElement>(null);
    const coverLetterUploadRef = useRef<HTMLDivElement>(null);
    const [uploadedFile, setUploadedFile] = useState<{
      cv: UploadedFile;
      coverLetter: UploadedFile;
    }>({
      cv: {
        name: '',
        size: 0,
        url: '',
        file: null
      },
      coverLetter: {
        name: '',
        size: 0,
        url: '',
        file: null
      },
    });

  const fileUploadMutation = useFileUpload();
  const applyToJobMutation = useApplyToJob();

  const [isBusy, setIsBusy] = useState(false);



//   jobId	Yes	string	The ID of the job being applied for. The job must be published and still open.
// cvUrl	Yes	string	URL to the applicant’s CV file.
// coverLetterUrl	No	string	URL to the cover letter file.
// education	Yes	array	At least one education record is required.
// workExperience	No	array	Optional work history entries.
// email	Yes	string	Applicant email address.
// firstName	Yes	string	Applicant first name.
// lastName	Yes	string	Applicant last name.
// country	Yes	string	Country of residence.
// phoneNumber	Yes	string	Phone number.
// linkedIn	No	string	LinkedIn profile URL.
// portfolio	No	string	Portfolio or personal website URL.

// Education Object

// Each item in the education array supports:
// Field	Required	Type	Notes
// schoolName	Yes	string	School or institution name.
// degree	Yes	string	Degree, certificate, or qualification.
// discipline	Yes	string	Field of study.
// optionGrade	No	string	Grade/classification such as Second Class Upper.
// startYear	Yes	integer	Start year of study.
// endYear	No	integer	End year of study.
// currentlyStudying	No	boolean	Use true if still studying.
// location	No	string	Institution location.
// description	No	string	Extra notes about the education record.

// Work Experience Object

// Each item in the workExperience array supports:
// Field	Required	Type	Notes
// companyName	Yes	string	Company or organization name.
// role	Yes	string	Job title or role.
// startYear	Yes	integer	Start year of the role.
// endYear	No	integer	End year of the role.
// currentlyWorking	No	boolean	Use true if this is a current role.
// location	No	string	Work location.
// description	No	string	Role summary.
// responsibilities	No	array of strings	Main responsibilities or duties.
// achievements	No	array of strings	Optional achievements or highlights.

  const countries: ParsedCountry[] = defaultCountries.map((country) => parseCountry(country));

  const countryOptions = countries.map((country) => ({
    label: country.name,
    value: country.name
  }));

const DEFAULT_FORM_VALUES: JobApplication = {
  jobId: jobId,
  cvUrl: '',
  coverLetterUrl: '',
  education: [],
  workExperience: [],
  email: '',
  firstName: '',
  lastName: '',
  country: '',
  phoneNumber: '',
  linkedIn: '',
  portfolio: '',
};

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<JobApplication, unknown, JobApplication>({
    resolver: zodResolver(JobApplicationSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'all',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'education',
  });

  const { fields: workFields, append: appendWork, remove: removeWork } = useFieldArray({
    control,
    name: 'workExperience',
  });

  const handleFileSelection = (files: File[] | null, key:string) => {
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    const fileUrl = URL.createObjectURL(file);

    setUploadedFile((prev) => ({
      ...prev,
      [key]: {
        name: file.name,
        size: file.size,
        url: fileUrl,
        file: file
      },
    }));
    setValue(key === 'cv' ? 'cvUrl' : 'coverLetterUrl', fileUrl, { shouldDirty: true });
  };

    const handleSubmitApplication = async(data: JobApplication) => {
      try {
      setIsBusy(true);

        const cvFile = uploadedFile.cv.file;
        const coverLetterFile = uploadedFile.coverLetter.file;

        if(!cvFile) {
          toast.error('CV/Resume file is required. Please upload your CV/Resume before submitting the application.');
          return;
        }

        const [cvUploadResult, coverLetterUploadResult] = await Promise.all([
          cvFile ? fileUploadMutation.mutateAsync({ file: cvFile, category: 'RESUME' }) : Promise.resolve(null),
          coverLetterFile ? fileUploadMutation.mutateAsync({ file: coverLetterFile, category: 'COVER_LETTER' }) : Promise.resolve(null)
        ]);

        const payload = {
  ...data,
  cvUrl: cvUploadResult?.data?.fileUrl || '',
  coverLetterUrl: coverLetterUploadResult?.data?.fileUrl || ''
};

        await applyToJobMutation.mutateAsync(payload);        
        reset(DEFAULT_FORM_VALUES);
        toast.success('Application submitted successfully.');
      
      } catch (error) {
        console.error('Error submitting application:', error);
        toast.error('Unable to submit application. Please try again.');
      } finally {
        setIsBusy(false);
      }
      
    };    


  return (
    <div>
           <form className="mt-6 grid grid-cols-1 gap-4" onSubmit={handleSubmit(data => {handleSubmitApplication(data)})}>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={1} title="Basic Information" />

            <div className="grid grid-cols-1 gap-6 gap-y-8 ">
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    label="First Name"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.firstName?.message ? String(errors.firstName.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter first name"                    
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Last Name"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.lastName?.message ? String(errors.lastName.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter last name"
                    
                  />
                )}
              />
   
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Email"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.email?.message ? String(errors.email.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter email"
                    
                  />
                )}
              />
              <div className="grid w-full grid-cols-1 items-end gap-6 gap-y-8 md:grid-cols-2">
  <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <CustomPhoneInput
                    label="Phone Number"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.phoneNumber?.message ? String(errors.phoneNumber.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter phone number"

                  />
                )}
              />
            <Controller
                name="country"
                control={control}
                render={({ field }) => (
                    <CustomSelect
                    label="Country"
                    required
                    {...field}
                      options={countryOptions}
                      placeholder="Select country"
                      disabled={isBusy}
                      isClearable={true}
                      className="w-full"
                      error={errors.country?.message ? String(errors.country.message) : undefined}
                    />
      
                )}
              />
              </div>
            
              <Controller
                name="linkedIn"
                control={control}
                render={({ field }) => (
                  <Input
                    label="LinkedIn"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.linkedIn?.message ? String(errors.linkedIn.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter LinkedIn profile URL"
                    
                  />
                )}
              />
              <Controller
                name="portfolio"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Portfolio"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.portfolio?.message ? String(errors.portfolio.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter portfolio URL"
                    
                  />
                )}
              />        
            </div>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">

         

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={2} title="Resume/CV" />

            <div className="grid grid-cols-1 items-center border border-gray-300 p-4 rounded-lg gap-4 bg-[f3f4f66b]">
              <div className="flex flex-col items-center justify-center gap-4 h-full">
                <Controller
                  name="cvUrl"
                  control={control}
                  render={({ field }) => (
                     <>
                  <FileUpload
                    {...field}
                  error={errors.cvUrl?.message ? String(errors.cvUrl.message) : undefined}
                    ref={cvUploadRef}
                    onChange={(files) => handleFileSelection(files, 'cv')}
                    disabled={isBusy}
                    allowedFileTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                    $maxSizeMB={10}
                    placeholder=''
                    containerClassName='flex flex-col items-center justify-center gap-3 text-center bg-gray-50 border-gray-200'
                  >
                    <LucideFiles className='h-8 w-8 ' strokeWidth={1} />
                    <Paragraph className='font-semibold text-gray-700'>Drag file to upload</Paragraph>
                    <Paragraph className='text-xs text-gray-500'>Supported document files: PDF, DOC, DOCX. Max size: 10MB</Paragraph>
                    <Button type='button' variant='secondary'>
                      Browse File
                    </Button>
                  </FileUpload>

                  <input type='hidden' value={field.value || ''} onChange={field.onChange} />
                </>
                  )}
                />

             
              </div>
            </div>
          </section>
     
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={3} title="Cover Letter" />

            <div className="grid grid-cols-1 items-center border border-gray-300 p-4 rounded-lg gap-4 bg-[f3f4f66b]">
              <div className="flex flex-col items-center justify-center gap-4 h-full">
                <Controller
                  name="coverLetterUrl"
                  control={control}
                  render={({ field }) => (
                    <>
                  <FileUpload
                  {...field}
                  error={errors.coverLetterUrl?.message ? String(errors.coverLetterUrl.message) : undefined}
                    ref={coverLetterUploadRef}
                    onChange={(files) => handleFileSelection(files, 'coverLetter')}
                    disabled={isBusy}
                    allowedFileTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                    $maxSizeMB={10}
                    placeholder=''
                    containerClassName='flex flex-col items-center justify-center gap-3 text-center bg-gray-50 border-gray-200'
                  >
                    <LucideFiles className='h-8 w-8 ' strokeWidth={1} />
                    <Paragraph className='font-semibold text-gray-700'>Drag file to upload</Paragraph>
                    <Paragraph className='text-xs text-gray-500'>Supported document files: PDF, DOC, DOCX. Max size: 10MB</Paragraph>
                    <Button type='button' variant='secondary'>
                      Browse File
                    </Button>
                  </FileUpload>

                  <input type='hidden' value={field.value || ''} onChange={field.onChange} />
                </>
                  )}
                />
              </div>

            </div>
          </section>

 </div>
        

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={4} title="Education" />

              <div className="space-y-6">
              {fields.map((field, index) => {
                const rowError = errors.education?.[index];

                return (
               <div key={field.id} className="rounded-lg border border-gray-200 p-4">
  <div className="mb-4 flex items-center justify-between">
    <Paragraph className="font-medium">
      Education {index + 1}
    </Paragraph>

    <div
      className="rounded-md border border-gray-200 h-[2.75rem] w-[2.75rem] flex items-center justify-center hover:border-red-200 cursor-pointer hover:bg-red-50"
      onClick={() => remove(index)}
    >
      <Trash2 className="h-6 w-6 text-red-300" />
    </div>
  </div>

  <div className="grid gap-4 md:grid-cols-2">
    <Controller
      name={`education.${index}.schoolName`}
      control={control}
      render={({ field }) => (
        <Input
          {...field}
          label="School Name"
          required
          value={field.value}
          onChange={field.onChange}
          error={rowError?.schoolName?.message ? String(rowError.schoolName.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`education.${index}.degree`}
      control={control}
      render={({ field }) => (
        <Input
          {...field}
          label="Degree"
          required
          value={field.value}
          onChange={field.onChange}
          error={rowError?.degree?.message ? String(rowError.degree.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`education.${index}.discipline`}
      control={control}
      render={({ field }) => (
        <Input
        {...field}
          label="Discipline"
          required
          value={field.value}
          onChange={field.onChange}
          error={rowError?.discipline?.message ? String(rowError.discipline.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`education.${index}.optionGrade`}
      control={control}
      render={({ field }) => (
        <Input
        {...field}
          label="Grade/Class"
          value={field.value || ''}
          onChange={field.onChange}
          error={rowError?.optionGrade?.message ? String(rowError.optionGrade.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`education.${index}.startYear`}
      control={control}
      render={({ field }) => (
        <Input
        {...field}
          type="number"
          label="Start Year"
          required
          value={field.value}
          error={rowError?.startYear?.message ? String(rowError.startYear.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`education.${index}.endYear`}
      control={control}
      render={({ field }) => (
        <Input
        {...field}
          type="number"
          label="End Year"
          value={field.value ?? ''}
          error={rowError?.endYear?.message ? String(rowError.endYear.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`education.${index}.location`}
      control={control}
      render={({ field }) => (
        <Input
          label="Location"
          {...field}
          value={field.value || ''}
          onChange={field.onChange}
          error={rowError?.location?.message ? String(rowError.location.message) : undefined}
          $marginbottom="0"
        />
      )}
    />
  </div>

  <div className="mt-4 flex items-center gap-2">
    <Controller
      name={`education.${index}.currentlyStudying`}
      control={control}
      render={({ field }) => (
        <input
          type="checkbox"
          checked={field.value ?? false}
          onChange={(e) => field.onChange(e.target.checked)}
        />
      )}
    />
    <Paragraph>Currently Studying</Paragraph>
  </div>

  <div className="mt-4">
    <Controller
      name={`education.${index}.description`}
      control={control}
      render={({ field }) => (
        <Input
          label="Description"
          value={field.value || ''}
          onChange={field.onChange}
          error={rowError?.description?.message ? String(rowError.description.message) : undefined}
          $marginbottom="0"
        />
      )}
    />
  </div>
</div>
                );
              })}

              <Button
                type="button"
                variant="secondary"
                onClick={() => append({ 
  schoolName: '',
  degree: '',
  discipline: '',
  optionGrade: '',
  startYear: new Date().getFullYear().toString(),
  endYear: undefined,
  currentlyStudying: false,
  location: '',
  description: '', })}
                // disabled={isBusy}
                className="mt-1 inline-flex items-center gap-2 border-[#0F766E] text-[#0F766E] hover:bg-[#F0FDFA]"
              >
                <Plus className="h-4 w-4" /> Add {fields.length > 0 ? 'Another' : ''} Education
              </Button>

              {typeof errors.education?.message === 'string' ? (
                <Paragraph className="!text-xs text-red-600">{errors.education.message}</Paragraph>
              ) : null}
            </div>
          </section>        


              <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={5} title="Work Experience" />

            <div className="space-y-6">
              {workFields.map((field, index) => {
                const rowError = errors.workExperience?.[index];
                return (
             <div key={field.id} className="rounded-lg border border-gray-200 p-4">
  <div className="mb-4 flex items-center justify-between">
    <Paragraph className="font-medium">
      Work Experience {index + 1}
    </Paragraph>

    <div
      className="rounded-md border border-gray-200 h-[2.75rem] w-[2.75rem] flex items-center justify-center hover:border-red-200 cursor-pointer hover:bg-red-50"
      onClick={() => removeWork(index)}
    >
      <Trash2 className="h-6 w-6 text-red-300" />
    </div>
  </div>

  <div className="grid gap-4 md:grid-cols-2">
    <Controller
      name={`workExperience.${index}.companyName`}
      control={control}
      render={({ field }) => (
        <Input
            {...field}
          label="Company Name"
          required
          value={field.value}
          onChange={field.onChange}
          error={rowError?.companyName?.message ? String(rowError.companyName.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`workExperience.${index}.role`}
      control={control}
      render={({ field }) => (
        <Input
        {...field}
          label="Role"
          required
          value={field.value}
          onChange={field.onChange}
          error={rowError?.role?.message ? String(rowError.role.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`workExperience.${index}.startYear`}
      control={control}
      render={({ field }) => (
        <Input
        {...field}
          type="number"
          label="Start Year"
          required
          value={field.value}
          error={rowError?.startYear?.message ? String(rowError.startYear.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`workExperience.${index}.endYear`}
      control={control}
      render={({ field }) => (
        <Input
          type="number"
          label="End Year"
          {...field}
          value={field.value ?? ''}
          error={rowError?.endYear?.message ? String(rowError.endYear.message) : undefined}
          $marginbottom="0"
        />
      )}
    />

    <Controller
      name={`workExperience.${index}.location`}
      control={control}
      render={({ field }) => (
        <Input
          {...field}
          label="Location"
          value={field.value || ''}
          onChange={field.onChange}
          error={rowError?.location?.message ? String(rowError.location.message) : undefined}
          $marginbottom="0"
        />
      )}
    />
  </div>

  <div className="mt-4 flex items-center gap-2">
    <Controller
      name={`workExperience.${index}.currentlyWorking`}
      control={control}
      render={({ field }) => (
        <input
          type="checkbox"
          checked={field.value ?? false}
          onChange={(e) => field.onChange(e.target.checked)}
        />
      )}
    />
    <Paragraph>Currently Working Here</Paragraph>
  </div>

  <div className="mt-4">
    <Controller
      name={`workExperience.${index}.description`}
      control={control}
      render={({ field }) => (
        <Input
          label="Description"
          value={field.value || ''}
          onChange={field.onChange}
          error={rowError?.description?.message ? String(rowError.description.message) : undefined}
          $marginbottom="0"
        />
      )}
    />
  </div>

  <div className="mt-4">
    <Controller
      name={`workExperience.${index}.responsibilities`}
      control={control}
      render={({ field }) => (
        <Input
          label="Responsibilities (comma separated)"
          value={Array.isArray(field.value) ? field.value.join(', ') : ''}
          onChange={(data) =>
            field.onChange(
              data
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
            )
          }
          $marginbottom="0"
        />
      )}
    />
  </div>

  <div className="mt-4">
    <Controller
      name={`workExperience.${index}.achievements`}
      control={control}
      render={({ field }) => (
        <Input
          label="Achievements (comma separated)"
          value={Array.isArray(field.value) ? field.value.join(', ') : ''}
          onChange={(data) =>
            field.onChange(
              data
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
            )
          }
          $marginbottom="0"
        />
      )}
    />
  </div>
</div>
                );
              })}

              <Button
                type="button"
                variant="secondary"
                onClick={() => appendWork({ companyName: '',
  role: '',
  startYear: new Date().getFullYear().toString(),
  endYear: undefined,
  currentlyWorking: false,
  location: '',
  description: '',
  responsibilities: [],
  achievements: [], })}
                // disabled={isBusy}
                className="mt-2 inline-flex items-center gap-2 border-[#0F766E] text-[#0F766E] hover:bg-[#F0FDFA]"
              >
                <Plus className="h-4 w-4" /> Add {workFields.length > 0 ? 'Another' : ''} Work Experience
              </Button>

              {typeof errors.workExperience?.message === 'string' ? (
                <Paragraph className="mt-1 text-xs text-red-600">{errors.workExperience.message}</Paragraph>
              ) : null}
            </div>
          </section>

          <Button onClick={handleSubmit(handleSubmitApplication)} disabled={isBusy} className="ml-auto">
            {isBusy ? 'Submitting...' : 'Submit Application'}
          </Button>
        </form>
    </div>
  )
}

export default ApplicationForm